<template>
  <div class="glass-panel p-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-bold text-gray-900 dark:text-white">Cost by Warehouse</h2>
      <div class="flex gap-2">
        <button 
          @click="chartType = 'bar'"
          :class="chartType === 'bar' ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'"
          class="px-3 py-1 rounded-lg text-sm font-medium transition-colors"
        >
          Bar Chart
        </button>
        <button 
          @click="chartType = 'donut'"
          :class="chartType === 'donut' ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'"
          class="px-3 py-1 rounded-lg text-sm font-medium transition-colors"
        >
          Donut Chart
        </button>
      </div>
    </div>
    
    <div class="h-96 mb-6">
      <Bar v-if="chartType === 'bar' && barChartData" :data="barChartData" :options="barChartOptions" />
      <Doughnut v-else-if="chartType === 'donut' && donutChartData" :data="donutChartData" :options="donutChartOptions" />
    </div>
    
    <!-- Warehouse List -->
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-gray-200 dark:border-white/10">
            <th class="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Warehouse</th>
            <th class="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Size</th>
            <th class="text-right p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Cost</th>
            <th class="text-right p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">% of Total</th>
            <th class="text-right p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Queries</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="warehouse in sortedWarehouses" 
            :key="warehouse.name"
            class="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          >
            <td class="p-3 text-sm font-medium text-gray-900 dark:text-white">{{ warehouse.name }}</td>
            <td class="p-3 text-sm text-gray-600 dark:text-gray-400">{{ warehouse.size }}</td>
            <td class="p-3 text-sm text-right">
              <span class="font-bold" :class="getWarehouseCostColor(warehouse.cost)">
                {{ formatCurrency(warehouse.cost) }}
              </span>
            </td>
            <td class="p-3 text-sm text-right text-gray-600 dark:text-gray-400">{{ warehouse.percentOfTotal }}%</td>
            <td class="p-3 text-sm text-right text-gray-600 dark:text-gray-400">{{ warehouse.queryCount.toLocaleString() }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Bar, Doughnut } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

const props = defineProps<{
  warehouses: Array<{
    name: string
    size: string
    cost: number
    percentOfTotal: number
    queryCount: number
  }>
}>()

const { formatCurrency } = useCostFormatting()

const getWarehouseCostColor = (cost: number) => {
  if (cost < 1000) return 'text-green-600 dark:text-green-400'
  if (cost < 3000) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

const chartType = ref<'bar' | 'donut'>('bar')

const sortedWarehouses = computed(() => {
  return [...props.warehouses].sort((a, b) => b.cost - a.cost)
})

const barChartData = computed(() => {
  const top10 = sortedWarehouses.value.slice(0, 10)
  
  return {
    labels: top10.map(w => w.name),
    datasets: [{
      label: 'Cost ($)',
      data: top10.map(w => w.cost),
      backgroundColor: top10.map(w => {
        if (w.cost < 1000) return 'rgba(34, 197, 94, 0.7)'
        if (w.cost < 3000) return 'rgba(251, 191, 36, 0.7)'
        return 'rgba(239, 68, 68, 0.7)'
      }),
      borderColor: top10.map(w => {
        if (w.cost < 1000) return 'rgb(34, 197, 94)'
        if (w.cost < 3000) return 'rgb(251, 191, 36)'
        return 'rgb(239, 68, 68)'
      }),
      borderWidth: 2
    }]
  }
})

const barChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y' as const,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: 'rgba(17, 24, 39, 0.9)',
      titleColor: '#F3F4F6',
      bodyColor: '#D1D5DB',
      borderColor: 'rgba(139, 92, 246, 0.5)',
      borderWidth: 1,
      padding: 12,
      callbacks: {
        label: function(context: any) {
          return '$' + context.parsed.x.toFixed(2)
        }
      }
    }
  },
  scales: {
    x: {
      grid: {
        color: 'rgba(156, 163, 175, 0.1)'
      },
      ticks: {
        color: '#9CA3AF',
        callback: function(value: any) {
          return '$' + value
        }
      }
    },
    y: {
      grid: {
        display: false
      },
      ticks: {
        color: '#9CA3AF'
      }
    }
  }
}

const donutChartData = computed(() => {
  const top10 = sortedWarehouses.value.slice(0, 10)
  
  return {
    labels: top10.map(w => w.name),
    datasets: [{
      data: top10.map(w => w.cost),
      backgroundColor: [
        'rgba(139, 92, 246, 0.8)',
        'rgba(59, 130, 246, 0.8)',
        'rgba(6, 182, 212, 0.8)',
        'rgba(20, 184, 166, 0.8)',
        'rgba(34, 197, 94, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(249, 115, 22, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(168, 85, 247, 0.8)'
      ],
      borderWidth: 2,
      borderColor: '#1F2937'
    }]
  }
})

const donutChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right' as const,
      labels: {
        color: '#9CA3AF',
        padding: 15,
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      backgroundColor: 'rgba(17, 24, 39, 0.9)',
      titleColor: '#F3F4F6',
      bodyColor: '#D1D5DB',
      borderColor: 'rgba(139, 92, 246, 0.5)',
      borderWidth: 1,
      padding: 12,
      callbacks: {
        label: function(context: any) {
          const label = context.label || ''
          const value = '$' + context.parsed.toFixed(2)
          const percent = ((context.parsed / context.dataset.data.reduce((a: number, b: number) => a + b, 0)) * 100).toFixed(1) + '%'
          return label + ': ' + value + ' (' + percent + ')'
        }
      }
    }
  }
}
</script>
